import React from 'react';
import { FaChartLine, FaSearch, FaDatabase, FaUmbrellaBeach, FaLightbulb, FaLaptopCode,
         FaHandshake, FaChartBar, FaBuilding, FaLeaf, FaCalculator, FaLandmark,
         FaRoute, FaSeedling, FaBolt, FaFileContract } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Services = () => {
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const serviceCategories = [
    {
      category: "Development & Research",
      icon: FaChartLine,
      color: "blue",
      services: [
        {
          title: "Monitoring & Evaluation for Non-Profits",
          icon: FaChartLine,
          description: "Designing and implementing rigorous frameworks to track performance, assess outcomes, and enhance accountability for large-scale projects."
        },
        {
          title: "Research & Development Studies",
          icon: FaSearch,
          description: "Conducting evidence-based studies to inform strategies, policies, and interventions in the development sector."
        },
        {
          title: "Field Data Collection & Analysis",
          icon: FaDatabase,
          description: "Managing large-scale, multi-country data collection exercises using advanced digital tools (Kobo, ODK, SPSS, Stata, Excel) to deliver reliable, decision-ready insights."
        }
      ]
    },
    {
      category: "Business & Investment",
      icon: FaChartBar,
      color: "green",
      services: [
        {
          title: "Tourism Advisory",
          icon: FaUmbrellaBeach,
          description: "Providing insights and strategic support for sustainable tourism growth and investment in Zanzibar and mainland Tanzania."
        },
        {
          title: "Market Research & Intelligence",
          icon: FaLightbulb,
          description: "Delivering actionable insights on industry trends, consumer behavior, and competitive landscapes to guide business and investment strategies."
        },
        {
          title: "Investment Guidance",
          icon: FaChartBar,
          description: "Offering market intelligence and advisory to help investors identify opportunities and maximize returns in Tanzania."
        },
        {
          title: "Real Estate Consulting",
          icon: FaBuilding,
          description: "Advising on property acquisition, development, and management with a focus on high-value opportunities."
        }
      ]
    },
    {
      category: "Technology & Operations",
      icon: FaLaptopCode,
      color: "purple",
      services: [
        {
          title: "IT Consulting & Advisory",
          icon: FaLaptopCode,
          description: "Helping clients leverage technology for operational efficiency, digital transformation, and innovation, including systems design, automation, and capacity building."
        },
        {
          title: "Company Registration & Partnerships",
          icon: FaHandshake,
          description: "Assisting foreign and domestic companies in setting up operations, building strategic partnerships, and navigating regulatory processes."
        }
      ]
    },
    {
      category: "Public Sector & Strategy",
      icon: FaLandmark,
      color: "orange",
      services: [
        {
          title: "Public Sector Consulting",
          icon: FaLandmark,
          description: "Partnering with African governments to develop strategic plans, policies, and institutional partnerships that drive inclusive development."
        },
        {
          title: "Strategic Planning",
          icon: FaRoute,
          description: "Designing robust strategic plans for both public sector institutions and private enterprises to guide long-term growth and resilience."
        }
      ]
    },
    {
      category: "Sustainability & Resources",
      icon: FaLeaf,
      color: "teal",
      services: [
        {
          title: "Environmental Impact Assessments",
          icon: FaLeaf,
          description: "Guiding clients through compliance, sustainability, and environmental management practices."
        },
        {
          title: "Agriculture Consulting",
          icon: FaSeedling,
          description: "Drawing on a pool of experts to provide guidance on strengthening African food systems, agri-business development, and climate-smart agriculture."
        },
        {
          title: "Energy & Natural Resources",
          icon: FaBolt,
          description: "Advising on sustainable energy solutions, natural resource management, and green investment opportunities."
        }
      ]
    },
    {
      category: "Financial & Regulatory",
      icon: FaCalculator,
      color: "red",
      services: [
        {
          title: "Business & Tax Advisory",
          icon: FaCalculator,
          description: "Supporting enterprises with financial planning, tax compliance, and business optimization."
        },
        {
          title: "Tax & Regulatory Services",
          icon: FaFileContract,
          description: "Offering comprehensive support on navigating Tanzania's regulatory landscape while optimizing tax and compliance frameworks."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Header Section */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-primary-900 text-white py-20 lg:py-28 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl"></div>

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div className="inline-block mb-4" variants={fadeInUp}>
            <span className="bg-secondary-500 text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
              What We Offer
            </span>
          </motion.div>
          <motion.h1 className="text-4xl lg:text-6xl font-bold mb-6" variants={fadeInUp}>
            Our <span className="text-secondary-500">Core Services</span>
          </motion.h1>
          <motion.p className="text-lg lg:text-xl leading-relaxed max-w-4xl mx-auto opacity-90" variants={fadeInUp}>
            Tailored consulting services to achieve sustainable growth and measurable impact.
          </motion.p>

          {/* Stats */}
          <motion.div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto mt-12" variants={fadeInUp}>
            <div>
              <div className="text-4xl font-bold text-secondary-500 mb-2">16</div>
              <div className="text-sm text-gray-300">Core Services</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-secondary-500 mb-2">6</div>
              <div className="text-sm text-gray-300">Service Categories</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-secondary-500 mb-2">100+</div>
              <div className="text-sm text-gray-300">Projects Delivered</div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Services by Category */}
      {serviceCategories.map((category, categoryIndex) => {
        const CategoryIcon = category.icon;
        return (
          <section
            key={categoryIndex}
            className={`py-16 lg:py-20 ${categoryIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Category Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-900 rounded-2xl mb-4 shadow-lg">
                  <CategoryIcon className="text-3xl text-white" />
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 dark:text-white mb-3">
                  {category.category}
                </h2>
                <div className="w-24 h-1 bg-secondary-500 mx-auto rounded-full"></div>
              </div>

              {/* Services Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.services.map((service, serviceIndex) => {
                  const ServiceIcon = service.icon;
                  return (
                    <div
                      key={serviceIndex}
                      className="group bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-gray-100 dark:border-gray-700 hover:border-navy-900 dark:hover:border-navy-700"
                    >
                      {/* Icon */}
                      <div className="flex items-start mb-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-navy-900 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <ServiceIcon className="text-2xl text-white" />
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold mb-4 text-navy-900 dark:text-white group-hover:text-navy-900 dark:group-hover:text-secondary-400 transition-colors">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
                        {service.description}
                      </p>

                      {/* Decorative line */}
                      <div className="mt-6 h-1 w-0 group-hover:w-16 bg-navy-900 transition-all duration-500 rounded-full"></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* Call to Action Section */}
      <section className="py-16 lg:py-24 bg-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-200 mb-10 leading-relaxed">
            Let's discuss how our consulting services can help you achieve your goals and drive sustainable growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a
              href="/contact"
              className="inline-flex items-center px-10 py-4 bg-secondary-500 text-white rounded-full font-semibold text-lg hover:bg-secondary-600 hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Contact Us Today
              <span className="ml-2">→</span>
            </a>
            <a
              href="/about"
              className="inline-flex items-center px-10 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold text-lg hover:bg-white hover:text-navy-900 transition-all duration-300"
            >
              Learn More About Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;